import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@f2e/ui/components/base"
import { Edit, Plus, RefreshCw, Trash2 } from "lucide-react"
import {
  useCreateEvaluationCaseLabelApi,
  useUpdateEvaluationCaseLabelApi,
  useDeleteEvaluationCaseLabelApi,
} from "@/hooks/api/eval"
import useToast from "@/hooks/useToast"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import { EvaluationCaseLabel } from "@/types"
import {
  DEFAULT_LABEL_COLOR,
  LABEL_COLOR_PALETTE,
  getLabelChipStyle,
  getRandomLabelColor,
  normalizeLabelColor,
} from "./labelColors"

export interface ManageCaseLabelsModalProps {
  isOpen: boolean
  workspaceId?: string
  labels: EvaluationCaseLabel[]
  onClose: () => void
  onLabelsChanged: () => void
}

interface LabelColorInputProps {
  value: string
  onChange: (color: string) => void
}

const LabelColorInput = ({ value, onChange }: LabelColorInputProps) => {
  const { t } = useTranslation()
  const [draftColor, setDraftColor] = useState(value)
  const normalizedColor = normalizeLabelColor(draftColor)

  useEffect(() => {
    setDraftColor(value)
  }, [value])

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-md border text-white shadow-xs"
          style={getLabelChipStyle(normalizedColor ?? DEFAULT_LABEL_COLOR)}
          onClick={() => {
            const color = getRandomLabelColor()
            setDraftColor(color)
            onChange(color)
          }}
          aria-label={t("evaluation.evaluationTable.labels.manage.randomColor")}
          title={t("evaluation.evaluationTable.labels.manage.randomColor")}
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <input
          className="h-9 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-3 text-sm font-mono dark:border-slate-700 dark:bg-slate-900"
          value={draftColor}
          onChange={(event) => {
            setDraftColor(event.target.value)
            onChange(event.target.value)
          }}
          placeholder="#0969da"
          aria-label={t("evaluation.evaluationTable.labels.manage.color")}
        />
      </div>
      {!normalizedColor && (
        <div className="text-xs text-red-600 dark:text-red-400">
          {t("evaluation.evaluationTable.labels.manage.invalidColor")}
        </div>
      )}
      <div className="grid grid-cols-8 gap-1.5">
        {LABEL_COLOR_PALETTE.map((color) => (
          <button
            key={color}
            type="button"
            className="h-6 rounded-md border border-slate-200 ring-offset-2 focus:outline-none focus:ring-2 focus:ring-slate-900 dark:border-slate-700 dark:ring-offset-slate-950 dark:focus:ring-slate-200"
            style={{ backgroundColor: color }}
            onClick={() => {
              setDraftColor(color)
              onChange(color)
            }}
            aria-label={color}
            title={color}
          />
        ))}
      </div>
    </div>
  )
}

const ManageCaseLabelsModal = ({
  isOpen,
  workspaceId,
  labels,
  onClose,
  onLabelsChanged,
}: ManageCaseLabelsModalProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const [view, setView] = useState<"list" | "form">("list")
  const [editingLabel, setEditingLabel] = useState<EvaluationCaseLabel | null>(
    null
  )
  const [formName, setFormName] = useState("")
  const [formColor, setFormColor] = useState(DEFAULT_LABEL_COLOR)
  const [deleteLabel, setDeleteLabel] = useState<EvaluationCaseLabel | null>(
    null
  )
  const { fetchFn: createLabel } = useCreateEvaluationCaseLabelApi(workspaceId)
  const { fetchFn: updateLabel } = useUpdateEvaluationCaseLabelApi()
  const { fetchFn: removeLabel } = useDeleteEvaluationCaseLabelApi()

  useEffect(() => {
    if (!isOpen) {
      setView("list")
      setEditingLabel(null)
      setFormName("")
      setFormColor(DEFAULT_LABEL_COLOR)
    }
  }, [isOpen])

  const handleAdd = () => {
    setEditingLabel(null)
    setFormName("")
    setFormColor(DEFAULT_LABEL_COLOR)
    setView("form")
  }

  const handleEdit = (label: EvaluationCaseLabel) => {
    setEditingLabel(label)
    setFormName(label.name)
    setFormColor(label.color)
    setView("form")
  }

  const handleCancelForm = () => {
    setView("list")
    setEditingLabel(null)
    setFormName("")
    setFormColor(DEFAULT_LABEL_COLOR)
  }

  const handleSubmitForm = async () => {
    const name = formName.trim()
    const color = normalizeLabelColor(formColor)
    if (!name || !color || (!editingLabel && !workspaceId)) return
    try {
      if (editingLabel) {
        await updateLabel({
          urlBuilderParams: { labelId: editingLabel.id },
          jsonBody: { name, color },
        })
      } else {
        await createLabel({ jsonBody: { name, color } })
      }
      handleCancelForm()
      onLabelsChanged()
    } catch (error) {
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.labels.manage.errorTitle"),
          fallbackDescription: "",
        }),
      })
    }
  }

  const handleDelete = async () => {
    if (!deleteLabel) return
    try {
      await removeLabel({
        urlBuilderParams: { labelId: deleteLabel.id },
      })
      setDeleteLabel(null)
      onLabelsChanged()
    } catch (error) {
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("evaluation.evaluationTable.labels.manage.errorTitle"),
          fallbackDescription: "",
        }),
      })
    }
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[90vh] gap-0 overflow-hidden p-0 sm:max-w-3xl">
          <DialogHeader className="border-b border-gray-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-950">
            <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {view === "form"
                ? editingLabel
                  ? t("evaluation.evaluationTable.labels.manage.editTitle")
                  : t("evaluation.evaluationTable.labels.manage.addTitle")
                : t("evaluation.evaluationTable.labels.manage.title")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-gray-50 p-8 dark:bg-slate-900">
            {view === "list" ? (
              <div className="flex flex-col gap-6">
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-lg border border-blue-600 bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-xs transition-all hover:border-blue-700 hover:bg-blue-700 dark:border-blue-400 dark:bg-blue-400 dark:text-gray-900 dark:hover:border-blue-500 dark:hover:bg-blue-500"
                    onClick={handleAdd}
                  >
                    <Plus className="h-4 w-4" />
                    {t("evaluation.evaluationTable.labels.manage.add")}
                  </button>
                </div>
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
                  {labels.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                      {t("evaluation.evaluationTable.table.noLabels")}
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200 dark:divide-slate-800">
                      {labels.map((label) => (
                        <div
                          key={label.id}
                          className="flex min-h-[64px] items-center gap-3 px-5 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-slate-900"
                        >
                          <span
                            className="inline-flex min-w-0 max-w-[520px] items-center rounded-full border px-2.5 py-1 text-sm font-medium"
                            style={getLabelChipStyle(label.color)}
                          >
                            <span className="truncate">{label.name}</span>
                          </span>
                          <div className="min-w-0 flex-1" />
                          <button
                            type="button"
                            className="rounded-full p-2 text-blue-600 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900"
                            onClick={() => handleEdit(label)}
                            title={t(
                              "evaluation.evaluationTable.labels.manage.edit"
                            )}
                            aria-label={t(
                              "evaluation.evaluationTable.labels.manage.edit"
                            )}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            className="rounded-full p-2 text-red-500 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900"
                            onClick={() => setDeleteLabel(label)}
                            title={t(
                              "evaluation.evaluationTable.labels.manage.delete"
                            )}
                            aria-label={t(
                              "evaluation.evaluationTable.labels.manage.delete"
                            )}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <form
                className="mx-auto flex max-w-2xl flex-col gap-6 rounded-xl bg-white p-8 shadow-sm dark:bg-slate-950"
                onSubmit={(event) => {
                  event.preventDefault()
                  void handleSubmitForm()
                }}
              >
                <div className="rounded-md bg-slate-50 px-3 py-4 text-center dark:bg-slate-900">
                  <span
                    className="inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-sm font-medium"
                    style={getLabelChipStyle(formColor)}
                  >
                    <span className="truncate">
                      {formName.trim() ||
                        t("evaluation.evaluationTable.labels.manage.preview")}
                    </span>
                  </span>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t("evaluation.evaluationTable.labels.manage.name")}
                  </label>
                  <input
                    className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
                    value={formName}
                    onChange={(event) => setFormName(event.target.value)}
                    placeholder={t(
                      "evaluation.evaluationTable.labels.manage.namePlaceholder"
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {t("evaluation.evaluationTable.labels.manage.color")}
                  </label>
                  <LabelColorInput value={formColor} onChange={setFormColor} />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-700"
                    onClick={handleCancelForm}
                  >
                    {t("evaluation.evaluationTable.labels.manage.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                    disabled={
                      !formName.trim() || !normalizeLabelColor(formColor)
                    }
                  >
                    {editingLabel
                      ? t("evaluation.evaluationTable.labels.manage.save")
                      : t("evaluation.evaluationTable.labels.manage.create")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={!!deleteLabel}
        onOpenChange={(open) => !open && setDeleteLabel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("evaluation.evaluationTable.labels.delete.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("evaluation.evaluationTable.labels.delete.description", {
                name: deleteLabel?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t("evaluation.evaluationTable.labels.delete.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              {t("evaluation.evaluationTable.labels.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export default ManageCaseLabelsModal
